import { Extension, Editor, JSONContent } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import debounce from 'lodash/debounce';

export type AutoSavePayload = {
  json: JSONContent;
  htmlContent: string;
};
export interface AutoSaveOptions {
  interval?: number;
  save: (payload: AutoSavePayload) => void | Promise<void>;
  valueTransformer?: (
    payload: AutoSavePayload,
    editor: Editor,
  ) => AutoSavePayload;
  onSaveSuccess?: () => void;
}
const isDocEmpty = (doc: any) => {
  return (
    doc.content.size === 0 || (doc.childCount === 1 && doc.textContent === '')
  );
};

const DEFAULT_INTERVAL = 5000;

export const AutoSaveExtension = Extension.create<AutoSaveOptions>({
  name: 'autoSave',

  addOptions() {
    return {
      interval: DEFAULT_INTERVAL,
      save: async () => {},
      valueTransformer: (payload: AutoSavePayload) => payload,
    };
  },

  addProseMirrorPlugins() {
    const { interval, save, valueTransformer, onSaveSuccess } = this.options;
    let lastSavedDoc: any = null;
    const getValue = (editor: Editor) => {
      return {
        json: editor.getJSON(),
        htmlContent: editor.getHTML(),
      };
    };
    const saveChanges = (editor: Editor) => {
      const value = getValue(editor);
      const transformed = valueTransformer
        ? valueTransformer(value, editor)
        : value;

      const editorDocumentState = editor.state.doc;
      const result = save(transformed);

      if (result instanceof Promise) {
        result.then(() => {
          lastSavedDoc = editorDocumentState;
          onSaveSuccess?.();
        });
      } else {
        lastSavedDoc = editorDocumentState;
      }
    };

    const debouncedSave = debounce((editor: Editor) => {
      saveChanges(editor);
    }, interval ?? DEFAULT_INTERVAL);

    let editorInstance: Editor | null = null;
    let beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;

    return [
      new Plugin({
        view: () => {
          editorInstance = this.editor;
          lastSavedDoc = editorInstance.state.doc;

          beforeUnloadHandler = (_event: BeforeUnloadEvent) => {
            if (!editorInstance) {
              return;
            }
            const currentDoc = editorInstance.state.doc;
            const isLastSavedDocEmpty = isDocEmpty(lastSavedDoc);

            if (!isLastSavedDocEmpty && !currentDoc.eq(lastSavedDoc)) {
              debouncedSave.flush();
              _event.preventDefault();
              _event.returnValue = '';
            }
          };
          window.addEventListener('beforeunload', beforeUnloadHandler);

          return {
            update: (view, prevState) => {
              const isPrevDocEmpty = isDocEmpty(prevState.doc);
              const isLastSavedDocEmpty = isDocEmpty(lastSavedDoc);

              if (isPrevDocEmpty && isLastSavedDocEmpty) {
                lastSavedDoc = view.state.doc;
              }

              if (!view.state.doc.eq(prevState.doc) && !isPrevDocEmpty) {
                debouncedSave(this.editor);
              }
            },
            destroy: () => {
              if (editorInstance) {
                const currentDoc = editorInstance.state.doc;
                const isLastSavedDocEmpty = isDocEmpty(lastSavedDoc);

                if (!isLastSavedDocEmpty && !currentDoc.eq(lastSavedDoc)) {
                  debouncedSave.flush();
                }
              }
              if (beforeUnloadHandler) {
                window.removeEventListener('beforeunload', beforeUnloadHandler);
              }
            },
          };
        },
      }),
    ];
  },
});
